import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import type { ApiResponse, Document } from '../types';

export type DocumentFilters = {
  dateFrom?: string;
  dateTo?: string;
  amountMin?: number;
  amountMax?: number;
  counterpartyName?: string;
  unlinked?: boolean;
  fiscalYear?: number;
};

function buildQuery(filters: DocumentFilters): string {
  const params = new URLSearchParams();
  if (filters.dateFrom) params.set('dateFrom', filters.dateFrom);
  if (filters.dateTo) params.set('dateTo', filters.dateTo);
  if (filters.amountMin !== undefined) params.set('amountMin', String(filters.amountMin));
  if (filters.amountMax !== undefined) params.set('amountMax', String(filters.amountMax));
  if (filters.counterpartyName) params.set('counterpartyName', filters.counterpartyName);
  if (filters.unlinked) params.set('unlinked', 'true');
  if (filters.fiscalYear !== undefined) params.set('fiscalYear', String(filters.fiscalYear));
  const q = params.toString();
  return q ? `?${q}` : '';
}

export function useDocuments(filters: DocumentFilters = {}) {
  return useQuery({
    queryKey: ['documents', filters],
    queryFn: async () => {
      const res = await api.get<ApiResponse<Document[]>>(`/api/documents${buildQuery(filters)}`);
      return res.data;
    },
  });
}

export type DocumentUploadInput = {
  file: File;
  documentDate: string;
  amount?: number;
  counterpartyName?: string;
  journalEntryId?: string;
};

export function useUploadDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: DocumentUploadInput) => {
      const fd = new FormData();
      fd.append('file', input.file);
      fd.append('documentDate', input.documentDate);
      if (input.amount !== undefined) fd.append('amount', String(input.amount));
      if (input.counterpartyName) fd.append('counterpartyName', input.counterpartyName);
      if (input.journalEntryId) fd.append('journalEntryId', input.journalEntryId);
      return api.post<ApiResponse<Document>>('/api/documents', fd);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['documents'] }),
  });
}

export function useUpdateDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...input
    }: {
      id: string;
      documentDate?: string;
      amount?: number | null;
      counterpartyName?: string | null;
      journalEntryId?: string | null;
    }) => api.patch<ApiResponse<Document>>(`/api/documents/${id}`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['documents'] }),
  });
}
